import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  theme,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  DatePicker,
  Card,
  Row,
  Col,
  Typography,
  Dropdown,
  MenuProps,
  App,
  Modal,
  InputNumber,
  Tabs,
} from 'antd';
import {
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MoreOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { CardRenewalDraftStatus, CardRenewalDraftFilters, CardRenewalDraftWithUser } from '@mygate/common';
import { cardRenewalService } from '@/services/cardRenewal';
import { CardRenewalStats } from '@/components/CardRenewalStats';
import { CardRenewalDetailModal } from '@/components/CardRenewalDetailModal';
import { UserDetailModal } from '@/components/UserDetailModal';
import { CardRenewalDocumentationTab } from '@/components/CardRenewalDocumentationTab';
import { ColumnsType } from 'antd/es/table';
import { formatDateTime, formatDate } from '@/utils/date';
import type { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

export function CardRenewalsPage() {
  const { token } = theme.useToken();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<CardRenewalDraftFilters>({});
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [selectedDraft, setSelectedDraft] = useState<CardRenewalDraftWithUser | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [userDetailModalVisible, setUserDetailModalVisible] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [triggerModalVisible, setTriggerModalVisible] = useState(false);
  const [triggerDays, setTriggerDays] = useState<number>(60);
  const [triggerBatchSize, setTriggerBatchSize] = useState<number>(100);
  const [activeTab, setActiveTab] = useState('queue');
  const { modal, message } = App.useApp();

  // Fetch statistics
  const { data: statsResponse, isLoading: statsLoading } = useQuery({
    queryKey: ['card-renewal-stats'],
    queryFn: () => cardRenewalService.getStatistics(),
    refetchInterval: 30000,
  });

  // Fetch drafts
  const {
    data: draftsResponse,
    isLoading: draftsLoading,
    refetch,
  } = useQuery({
    queryKey: ['card-renewals', filters],
    queryFn: () => cardRenewalService.listDrafts(filters),
  });

  const drafts = draftsResponse?.data || [];
  const stats = statsResponse?.data || null;

  // Bulk approve mutation
  const bulkApproveMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(ids.map(id => cardRenewalService.approveDraft(id)));
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failedResults = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');

      // Extract error messages from failed results
      const errorMessages = failedResults.map(r => {
        const err = r.reason;
        return err instanceof Error ? err.message : 'Unknown error';
      });

      return { succeeded, failed: failedResults.length, errorMessages };
    },
    onSuccess: data => {
      if (data.succeeded > 0) {
        message.success(`${data.succeeded} draft(s) approved`);
      }
      if (data.failed > 0) {
        // Show first error message (unique errors would be better but this is simpler)
        const errorDetail = data.errorMessages[0] || 'Unknown error';
        message.error(`${data.failed} draft(s) failed: ${errorDetail}`);
      }
      queryClient.invalidateQueries({ queryKey: ['card-renewals'] });
      queryClient.invalidateQueries({ queryKey: ['card-renewal-stats'] });
      setSelectedRowKeys([]);
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to approve drafts';
      message.error(errorMessage);
    },
  });

  // Bulk reject mutation
  const bulkRejectMutation = useMutation({
    mutationFn: async ({ ids, reason }: { ids: string[]; reason: string }) => {
      const results = await Promise.allSettled(ids.map(id => cardRenewalService.rejectDraft(id, reason)));
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failedResults = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');

      const errorMessages = failedResults.map(r => {
        const err = r.reason;
        return err instanceof Error ? err.message : 'Unknown error';
      });

      return { succeeded, failed: failedResults.length, errorMessages };
    },
    onSuccess: data => {
      if (data.succeeded > 0) {
        message.success(`${data.succeeded} draft(s) rejected`);
      }
      if (data.failed > 0) {
        const errorDetail = data.errorMessages[0] || 'Unknown error';
        message.error(`${data.failed} draft(s) failed: ${errorDetail}`);
      }
      queryClient.invalidateQueries({ queryKey: ['card-renewals'] });
      queryClient.invalidateQueries({ queryKey: ['card-renewal-stats'] });
      setSelectedRowKeys([]);
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reject drafts';
      message.error(errorMessage);
    },
  });

  // Trigger check mutation
  const triggerCheckMutation = useMutation({
    mutationFn: (options: { daysBeforeExpiration?: number; batchSize?: number }) =>
      cardRenewalService.triggerExpirationCheck(options),
    onSuccess: response => {
      message.success(response.data?.message || 'Expiration check triggered');
      queryClient.invalidateQueries({ queryKey: ['card-renewals'] });
      queryClient.invalidateQueries({ queryKey: ['card-renewal-stats'] });
      setTriggerModalVisible(false);
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to trigger expiration check';
      message.error(errorMessage);
    },
  });

  // Filter drafts based on search text
  const filteredDrafts = useMemo(() => {
    if (!searchText) return drafts;

    const searchLower = searchText.toLowerCase();
    return drafts.filter(draft => {
      if (draft.id?.toLowerCase().includes(searchLower)) return true;
      if (draft.userId?.toLowerCase().includes(searchLower)) return true;
      if (draft.deliveryAddress?.city?.toLowerCase().includes(searchLower)) return true;
      if (draft.deliveryAddress?.street?.toLowerCase().includes(searchLower)) return true;
      if (draft.nameOnCard?.line1?.toLowerCase().includes(searchLower)) return true;
      return false;
    });
  }, [drafts, searchText]);

  const handleBulkApprove = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select drafts to approve');
      return;
    }

    modal.confirm({
      title: 'Approve Drafts',
      content: `Are you sure you want to approve ${selectedRowKeys.length} draft(s)?`,
      onOk: () => {
        bulkApproveMutation.mutate(selectedRowKeys);
      },
    });
  };

  const handleBulkReject = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select drafts to reject');
      return;
    }

    let rejectReason = '';
    modal.confirm({
      title: 'Reject Drafts',
      content: (
        <div>
          <p>Are you sure you want to reject {selectedRowKeys.length} draft(s)?</p>
          <Input
            placeholder="Rejection reason"
            onChange={e => (rejectReason = e.target.value)}
            style={{ marginTop: 10 }}
          />
        </div>
      ),
      onOk: () => {
        if (!rejectReason.trim()) {
          message.error('Please provide a rejection reason');
          return Promise.reject();
        }
        bulkRejectMutation.mutate({ ids: selectedRowKeys, reason: rejectReason });
      },
    });
  };

  const handleViewDetails = (draft: CardRenewalDraftWithUser) => {
    setSelectedDraft(draft);
    setDetailModalVisible(true);
  };

  const handleViewUserDetails = (userId: string) => {
    setSelectedUserId(userId);
    setUserDetailModalVisible(true);
  };

  const getStatusColor = (status: CardRenewalDraftStatus) => {
    switch (status) {
      case CardRenewalDraftStatus.DRAFT:
        return 'default';
      case CardRenewalDraftStatus.APPROVED:
        return 'processing';
      case CardRenewalDraftStatus.PROCESSING:
        return 'processing';
      case CardRenewalDraftStatus.COMPLETED:
        return 'success';
      case CardRenewalDraftStatus.REJECTED:
        return 'error';
      case CardRenewalDraftStatus.CANCELLED:
        return 'default';
      default:
        return 'default';
    }
  };

  const columns: ColumnsType<CardRenewalDraftWithUser> = [
    {
      title: 'Kunde & Entwurf',
      dataIndex: 'userId',
      key: 'userId',
      width: 180,
      render: (_: string, record: CardRenewalDraftWithUser) => {
        const name = record.user ? `${record.user.firstName || ''} ${record.user.lastName || ''}`.trim() : '';
        // Fallback: name → phoneNumber → truncated userId
        const displayName = name || record.user?.phoneNumber || record.userId.slice(-8);

        return (
          <div className="table-cell-stack">
            <Button
              className="table-cell-link"
              type="link"
              onClick={() => handleViewUserDetails(record.userId)}
              style={{ padding: 0, height: 'auto' }}
            >
              <Space wrap>
                <UserOutlined />
                <Typography.Text strong style={{ color: token.colorPrimary }}>
                  {displayName}
                </Typography.Text>
              </Space>
            </Button>
            <Button type="link" className="table-cell-link table-cell-muted" onClick={() => handleViewDetails(record)}>
              Entwurf {record.id.slice(-8)}
            </Button>
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: CardRenewalDraftStatus) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: 'Card Expires',
      dataIndex: 'cardExpirationDate',
      key: 'cardExpirationDate',
      width: 130,
      render: (date: string) => formatDate(date),
      sorter: (a, b) => new Date(a.cardExpirationDate).getTime() - new Date(b.cardExpirationDate).getTime(),
    },
    {
      title: 'Karte & Versand',
      key: 'delivery',
      width: 240,
      render: (_, record) => (
        <div className="table-cell-stack">
          <Typography.Text strong>{record.nameOnCard?.line1 || '—'}</Typography.Text>
          <Typography.Text className="table-cell-muted">
            {record.deliveryAddress
              ? `${record.deliveryAddress.street}, ${record.deliveryAddress.city}`
              : 'Keine Adresse'}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: Date) => formatDateTime(date),
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => {
        const items: MenuProps['items'] = [
          {
            key: 'view',
            label: 'View Details',
            onClick: () => handleViewDetails(record),
          },
        ];

        if (record.status === CardRenewalDraftStatus.DRAFT) {
          items.push(
            {
              key: 'approve',
              label: 'Approve',
              onClick: () => {
                modal.confirm({
                  title: 'Approve Draft',
                  content: 'Are you sure you want to approve this card renewal?',
                  onOk: async () => {
                    try {
                      await cardRenewalService.approveDraft(record.id);
                      message.success('Draft approved');
                      refetch();
                      queryClient.invalidateQueries({ queryKey: ['card-renewal-stats'] });
                    } catch (error) {
                      const errorMessage = error instanceof Error ? error.message : 'Failed to approve draft';
                      message.error(errorMessage);
                    }
                  },
                });
              },
            },
            {
              key: 'reject',
              label: 'Reject',
              danger: true,
              onClick: () => {
                let rejectReason = '';
                modal.confirm({
                  title: 'Reject Draft',
                  content: (
                    <div>
                      <p>Are you sure you want to reject this card renewal?</p>
                      <Input
                        placeholder="Rejection reason"
                        onChange={e => (rejectReason = e.target.value)}
                        style={{ marginTop: 10 }}
                      />
                    </div>
                  ),
                  onOk: async () => {
                    if (!rejectReason.trim()) {
                      message.error('Please provide a rejection reason');
                      return Promise.reject();
                    }
                    try {
                      await cardRenewalService.rejectDraft(record.id, rejectReason);
                      message.success('Draft rejected');
                      refetch();
                      queryClient.invalidateQueries({ queryKey: ['card-renewal-stats'] });
                    } catch (error) {
                      const errorMessage = error instanceof Error ? error.message : 'Failed to reject draft';
                      message.error(errorMessage);
                    }
                  },
                });
              },
            },
          );
        }

        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <Button icon={<MoreOutlined />} size="small" />
          </Dropdown>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys as string[]);
    },
    getCheckboxProps: (record: CardRenewalDraftWithUser) => ({
      disabled: record.status !== CardRenewalDraftStatus.DRAFT,
    }),
  };

  const handleStatusFilterChange = (value: CardRenewalDraftStatus) => {
    setFilters(prev => ({ ...prev, status: value || undefined }));
  };

  const handleDateRangeChange = (dates: null | [Dayjs | null, Dayjs | null]) => {
    if (dates && dates[0] && dates[1]) {
      setFilters(prev => ({
        ...prev,
        startDate: dates[0]!.format('YYYY-MM-DD'),
        endDate: dates[1]!.format('YYYY-MM-DD'),
      }));
    } else {
      setFilters(prev => {
        const { startDate: _s, endDate: _e, ...rest } = prev;
        return rest;
      });
    }
  };

  return (
    <>
      <div className="page-toolbar">
        <Typography.Text type="secondary">Kartenverlängerungen prüfen und bearbeiten.</Typography.Text>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
          <Button type="primary" icon={<ThunderboltOutlined />} onClick={() => setTriggerModalVisible(true)}>
            Trigger Check
          </Button>
        </Space>
      </div>

      <>
        <CardRenewalStats stats={stats} loading={statsLoading} />

        <Card>
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <Tabs.TabPane tab="Renewals Queue" key="queue">
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                {/* Filters */}
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12} xl={5}>
                    <Select
                      placeholder="Filter by status"
                      style={{ width: '100%' }}
                      onChange={handleStatusFilterChange}
                      value={filters.status}
                      allowClear
                    >
                      {Object.values(CardRenewalDraftStatus).map(status => (
                        <Select.Option key={status} value={status}>
                          {status}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>
                  <Col xs={24} md={12} xl={7}>
                    <RangePicker
                      style={{ width: '100%' }}
                      onChange={handleDateRangeChange}
                      format="YYYY-MM-DD"
                      placeholder={['Expiration from', 'Expiration to']}
                    />
                  </Col>
                  <Col xs={24} md={12} xl={8}>
                    <Input
                      placeholder="Search (ID/user/address/name)"
                      value={searchText}
                      onChange={e => setSearchText(e.target.value)}
                    />
                  </Col>
                  <Col xs={24} md={12} xl={4}>
                    <Button
                      onClick={() => {
                        setFilters({});
                        setSearchText('');
                      }}
                    >
                      Clear Filters
                    </Button>
                  </Col>
                </Row>

                {/* Bulk Actions */}
                <Space wrap>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleBulkApprove}
                    disabled={selectedRowKeys.length === 0}
                    loading={bulkApproveMutation.isPending}
                  >
                    Bulk Approve ({selectedRowKeys.length})
                  </Button>
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={handleBulkReject}
                    disabled={selectedRowKeys.length === 0}
                    loading={bulkRejectMutation.isPending}
                  >
                    Bulk Reject ({selectedRowKeys.length})
                  </Button>
                </Space>

                {/* Table */}
                <Table
                  rowSelection={rowSelection}
                  columns={columns}
                  dataSource={filteredDrafts}
                  rowKey="id"
                  loading={draftsLoading}
                  pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showTotal: total => `Total ${total} drafts`,
                  }}
                  tableLayout="fixed"
                  scroll={{ x: 940 }}
                />
              </Space>
            </Tabs.TabPane>
            <Tabs.TabPane tab="Documentation" key="docs">
              <CardRenewalDocumentationTab />
            </Tabs.TabPane>
          </Tabs>
        </Card>
      </>

      <CardRenewalDetailModal
        visible={detailModalVisible}
        draft={selectedDraft}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedDraft(null);
        }}
        onActionComplete={() => {
          refetch();
          queryClient.invalidateQueries({ queryKey: ['card-renewal-stats'] });
        }}
      />

      <Modal
        title="Trigger Expiration Check"
        open={triggerModalVisible}
        onCancel={() => setTriggerModalVisible(false)}
        onOk={() => triggerCheckMutation.mutate({ daysBeforeExpiration: triggerDays, batchSize: triggerBatchSize })}
        confirmLoading={triggerCheckMutation.isPending}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <label style={{ display: 'block', marginBottom: 8 }}>Days before expiration:</label>
            <InputNumber
              min={1}
              max={365}
              value={triggerDays}
              onChange={v => setTriggerDays(v || 60)}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8 }}>Batch size:</label>
            <InputNumber
              min={1}
              max={1000}
              value={triggerBatchSize}
              onChange={v => setTriggerBatchSize(v || 100)}
              style={{ width: '100%' }}
            />
          </div>
          <p style={{ color: '#8c8c8c', fontSize: 12 }}>
            This will scan for cards expiring within the specified days and create renewal drafts for review.
          </p>
        </Space>
      </Modal>

      <UserDetailModal
        userId={selectedUserId}
        visible={userDetailModalVisible}
        onClose={() => {
          setUserDetailModalVisible(false);
          setSelectedUserId(null);
        }}
      />
    </>
  );
}
